/**
 * =============================================================================
 * THE BACKEND CONTRACT — single source of truth
 * =============================================================================
 *
 * This file defines the interface between the Elite SPA frontend and ANY backend
 * implementation. The FastAPI service in /backend implements it. A Google Apps
 * Script Web App implements it. Anything else that wants to serve this site
 * implements it too.
 *
 * **If a backend disagrees with this file, the backend is wrong.**
 *
 * Nothing here imports React, axios, or anything environment-specific. It is
 * plain data shapes and pure functions, so it can be read by a person writing a
 * backend in another language and copied into a test without dragging the app
 * along with it.
 *
 * -----------------------------------------------------------------------------
 * WHY THE VALIDATORS LIVE HERE AND NOT IN THE ADAPTERS
 * -----------------------------------------------------------------------------
 * Every adapter needs the same answer to "is this submission valid?". If each
 * one carried its own copy, they would drift, and the UI would start behaving
 * differently depending on which backend happened to be selected — which is
 * precisely what this layer exists to prevent. The adapters import from here.
 */

// -----------------------------------------------------------------------------
// BOOKING SUBMISSION SHAPE
// -----------------------------------------------------------------------------
/**
 * What the frontend sends. Field names are the wire format — a backend reads
 * exactly these keys.
 *
 * @typedef  {Object} BookingSubmission
 * @property {string} name           Required. Free text, as typed.
 * @property {string} phone          Required. Free text, as typed — NOT normalised
 *                                   or validated for format. People write numbers
 *                                   in many shapes and a spa can read all of them;
 *                                   rejecting a valid number is worse than
 *                                   accepting a messy one.
 * @property {string} service        Required. The service NAME as displayed, in
 *                                   the visitor's current language — not an id.
 *                                   See the note on i18n below.
 * @property {string} date           Required. ISO `yyyy-MM-dd`.
 * @property {string} time           Required. As displayed, e.g. "2:30 PM".
 * @property {string} [email]        OPTIONAL. Added in fix pass 2. May be an empty
 *                                   string. Never blocks submission.
 * @property {string} [notes]        Optional. Free text. May be an empty string.
 */

/**
 * NOTE ON `service` AND i18n — deliberate, and worth knowing before you write a
 * backend. The site is trilingual (EN / 中文 / ES) and the form submits the
 * service NAME as the visitor saw it, so a Chinese visitor's booking arrives with
 * a Chinese service name. That is preserved from the existing behaviour, and it
 * is arguably right for a human reading the notification. A backend must NOT
 * assume it can match `service` against a fixed English list.
 */

/**
 * What every adapter returns. Both success and failure resolve — adapters do not
 * throw for an expected outcome.
 *
 * @typedef  {Object} BookingResult
 * @property {boolean} ok            true if the submission was delivered.
 * @property {string}  [id]          Present when ok. A backend reference if one
 *                                   was returned; may be absent — see the
 *                                   optimistic-resolution note in ./index.js.
 * @property {string}  [error]       Present when !ok. A short machine-ish reason
 *                                   for logging. NOT shown to the visitor — the
 *                                   UI owns its own copy, in three languages.
 */

/** Error codes an adapter may set on a failed BookingResult. */
export const ERRORS = {
  /** The request did not complete within the timeout. */
  TIMEOUT: "timeout",
  /** DNS failure, connection refused, offline, CORS block. */
  NETWORK: "network",
  /** The backend answered, but with a non-2xx status. */
  SERVER: "server",
  /** validateBooking() rejected the payload before any request was made. */
  INVALID: "invalid",
  /** No backend is configured — see ./index.js. */
  NOT_CONFIGURED: "not_configured",
};

// -----------------------------------------------------------------------------
// TIMEOUT
// -----------------------------------------------------------------------------
/**
 * Shared by every adapter so the visitor's experience does not change with the
 * backend behind it.
 *
 * 18s sits above a realistic worst case — a cold-started container plus a first
 * lazy database connection — while staying short enough that a visitor has not
 * already given up. Carried over from fix pass 1.
 */
export const REQUEST_TIMEOUT_MS = 18000;

// -----------------------------------------------------------------------------
// VALIDATION
// -----------------------------------------------------------------------------
/**
 * The five fields that must be present. `email` and `notes` are deliberately
 * absent: both are optional and neither may ever block a submission.
 */
export const REQUIRED_FIELDS = ["name", "phone", "service", "date", "time"];

/**
 * Is this submission complete enough to send?
 *
 * Presence only. There is intentionally no phone-format check and no email-format
 * check — see `isPlausibleEmail` below for why the email one is advisory.
 *
 * @param   {BookingSubmission} data
 * @returns {{ valid: boolean, missing: string[] }}
 */
export function validateBooking(data) {
  const missing = REQUIRED_FIELDS.filter((key) => {
    const value = data?.[key];
    return typeof value !== "string" ? !value : value.trim() === "";
  });
  return { valid: missing.length === 0, missing };
}

/**
 * A loose sanity check on an email address: something, an @, something, a dot,
 * something — with no whitespace.
 *
 * ADVISORY ONLY. Nothing in this codebase should refuse a submission because
 * this returned false. The email is an optional convenience for replying; a
 * visitor who mistypes it still gets their booking through, because their phone
 * number is the required contact route. Blocking a spa booking over a typo'd
 * optional field would be a worse outcome than an unreachable reply address.
 *
 * @param   {string} value
 * @returns {boolean} false for empty input, which callers should treat as "not
 *                    provided" rather than "invalid".
 */
export function isPlausibleEmail(value) {
  if (typeof value !== "string" || value.trim() === "") return false;
  return /^\S+@\S+\.\S+$/.test(value.trim());
}

/**
 * Produce the exact wire payload from raw form state.
 *
 * Every adapter calls this, so all backends receive byte-identical keys
 * regardless of which one is selected. Values are trimmed; optional fields
 * collapse to "" rather than undefined so the JSON shape is stable — a backend
 * can rely on every key existing.
 *
 * @param   {BookingSubmission} data
 * @returns {BookingSubmission}
 */
export function buildPayload(data) {
  const str = (v) => (typeof v === "string" ? v.trim() : "");
  return {
    name: str(data?.name),
    phone: str(data?.phone),
    email: str(data?.email),
    service: str(data?.service),
    date: str(data?.date),
    time: str(data?.time),
    notes: str(data?.notes),
  };
}

/**
 * =============================================================================
 * THE ADAPTER INTERFACE
 * =============================================================================
 *
 * Every adapter in this directory exports exactly one function with exactly this
 * signature. That sameness is the whole point: Booking.jsx calls it without
 * knowing or caring which implementation is behind it.
 *
 *   submitBooking(data: BookingSubmission) => Promise<BookingResult>
 *
 * Rules an adapter MUST follow:
 *
 *   1. RESOLVE, DO NOT THROW, for any expected outcome — including network
 *      failure and timeout. Return { ok: false, error } instead. The UI has one
 *      error path and should not need a try/catch as well as a result check.
 *
 *   2. CALL buildPayload() rather than hand-rolling the body, so the wire format
 *      cannot drift between adapters.
 *
 *   3. HONOUR REQUEST_TIMEOUT_MS. A visitor must never be left with a spinner
 *      that has no end.
 *
 *   4. NEVER return visitor-facing copy in `error`. The UI owns its wording, in
 *      three languages. `error` is for the console and for logs.
 */
